import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../hooks/useCompany';
import { usePermissions } from '../hooks/usePermissions';
import {
  LayoutDashboard,
  Users,
  Receipt,
  Wallet,
  FileText,
  Building2,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  DollarSign,
  TrendingUp,
  Droplets,
  Truck,
  Home,
  Tractor,
  UtensilsCrossed,
  Hotel,
  Landmark,
  Package,
  CreditCard,
  Briefcase,
  Route,
  UserPlus,
  CalendarDays,
  ClipboardList,
  PieChart,
  BarChart3,
  Target,
  Monitor,
  Sun,
  Moon,
} from 'lucide-react';

// Navigation structure matching the original ERP
const financeNavItems = [
  { name: 'Chart of Accounts', href: '/finance/accounts', icon: FileText, description: 'Manage account structure' },
  { name: 'Journal Entries', href: '/finance/journals', icon: Receipt, description: 'Record transactions' },
  { name: 'General Ledger', href: '/finance/general-ledger', icon: ClipboardList, description: 'View all postings' },
  { name: 'Trial Balance', href: '/finance/trial-balance', icon: TrendingUp, description: 'Account balances' },
  { name: 'Financial Statements', href: '/finance/statements', icon: PieChart, description: 'Income & Balance Sheet' },
  { name: 'Budgets', href: '/finance/budgets', icon: Wallet, description: 'Budget planning' },
  { name: 'Budget Performance', href: '/finance/budget-performance', icon: Target, description: 'Budget vs Actual' },
];

const hrNavItems = [
  { name: 'Employees', href: '/hr/employees', icon: Users, description: 'Staff management' },
  { name: 'Subsidiaries', href: '/hr/subsidiaries', icon: Building2, description: 'Organization subsidiaries' },
  { name: 'Recruitment', href: '/hr/recruitment', icon: UserPlus, description: 'Hiring pipeline' },
  { name: 'Leave Management', href: '/hr/leave', icon: CalendarDays, description: 'Time off requests' },
  { name: 'Payroll', href: '/hr/payroll', icon: DollarSign, description: 'Salary processing' },
];

const subsidiaryNavGroups = [
  {
    name: 'DEA Waters',
    icon: Droplets,
    color: 'text-blue-500',
    items: [
      { name: 'Production', href: '/waters/production', icon: Package },
      { name: 'Depot & Sales', href: '/waters/depot-sales', icon: CreditCard },
    ],
  },
  {
    name: 'DEA Financial Services',
    icon: Landmark,
    color: 'text-green-500',
    items: [
      { name: 'DFS Dashboard', href: '/dfs/dashboard', icon: LayoutDashboard },
      { name: 'Vault Allocations', href: '/dfs/allocations', icon: CreditCard },
      { name: 'Teller Accounts', href: '/dfs/teller-accounts', icon: Users },
      { name: 'Teller Dashboard', href: '/dfs/teller', icon: Monitor },
      { name: 'Stock Management', href: '/dfs/stock', icon: Package },
      { name: 'Customer Accounts', href: '/dfs/customers', icon: Users },
      { name: 'Loans', href: '/dfs/loans', icon: Briefcase },
    ],
  },
  {
    name: 'Liberrands',
    icon: Truck,
    color: 'text-orange-500',
    items: [
      { name: 'Orders', href: '/liberrands/orders', icon: Package },
      { name: 'Deliveries', href: '/liberrands/deliveries', icon: Route },
      { name: 'Agents', href: '/liberrands/agents', icon: Users },
    ],
  },
  {
    name: 'DEA Ranch',
    icon: Tractor,
    color: 'text-yellow-600',
    items: [
      { name: 'Operations', href: '/ranch/operations', icon: Tractor },
      { name: 'Livestock', href: '/ranch/livestock', icon: Package },
    ],
  },
  {
    name: 'DEA Kitchen',
    icon: UtensilsCrossed,
    color: 'text-red-500',
    items: [
      { name: 'Production', href: '/kitchen/production', icon: Package },
      { name: 'Sales Agents', href: '/kitchen/agents', icon: Users },
    ],
  },
  {
    name: 'Dorlee City View Hotel',
    icon: Hotel,
    color: 'text-purple-500',
    items: [
      { name: 'Rooms', href: '/hotel/rooms', icon: Home },
      { name: 'Guests', href: '/hotel/guests', icon: Users },
    ],
  },
];

const subsidiaryIcons = {
  'DEA-WTR': Droplets,
  'DEA-FIN': Landmark,
  'DEA-LIB': Truck,
  'DEA-RCH': Tractor,
  'DEA-KIT': UtensilsCrossed,
  'DEA-HTL': Hotel,
  'DEA-TRN': Truck,
  'DEA-RES': Home,
};

function NavDropdown({ trigger, icon: Icon, items, isOpen, onToggle, location }) {
  const isActive = items.some(item => location.pathname === item.href);

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
          isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        data-testid={`nav-dropdown-${trigger.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Icon className="h-4 w-4" />
        <span>{trigger}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-lg py-2 z-50 animate-fade-in">
          {items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                  location.pathname === item.href 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-foreground hover:bg-muted'
                }`}
                data-testid={`nav-link-${item.href.replace(/\//g, '-').slice(1)}`}
              >
                <ItemIcon className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">{item.name}</p>
                  {item.description && (
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SubsidiaryDropdown({ isOpen, onToggle, location }) {
  const allItems = subsidiaryNavGroups.flatMap(g => g.items);
  const isActive = allItems.some(item => location.pathname === item.href);

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
          isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        data-testid="nav-dropdown-subsidiaries"
      >
        <Building2 className="h-4 w-4" />
        <span>Subsidiaries</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-card border border-border rounded-lg shadow-lg py-2 z-50 animate-fade-in max-h-[70vh] overflow-y-auto">
          {subsidiaryNavGroups.map((group, idx) => {
            const GroupIcon = group.icon;
            return (
              <div key={group.name}>
                {idx > 0 && <div className="my-2 border-t border-border" />}
                <div className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wide ${group.color}`}>
                  <GroupIcon className="h-4 w-4" />
                  {group.name}
                </div>
                {group.items.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`flex items-center gap-3 px-6 py-2 text-sm transition-colors ${
                        location.pathname === item.href 
                          ? 'bg-primary/10 text-primary' 
                          : 'text-foreground hover:bg-muted'
                      }`}
                      data-testid={`nav-link-${item.href.replace(/\//g, '-').slice(1)}`}
                    >
                      <ItemIcon className="h-4 w-4 text-muted-foreground" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SidebarSection({ title, icon: Icon, items, isExpanded, onToggle, location, colorClass }) {
  const isActive = items.some(item => location.pathname === item.href);

  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        data-testid={`sidebar-section-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className={`flex items-center gap-3 ${colorClass || ''}`}>
          <Icon className="h-5 w-5" />
          <span>{title}</span>
        </div>
        <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
      </button>
      {isExpanded && (
        <div className="mt-1 ml-8 space-y-1">
          {items.map((item) => {
            const ItemIcon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  location.pathname === item.href
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid={`sidebar-link-${item.href.replace(/\//g, '-').slice(1)}`}
              >
                <ItemIcon className="h-4 w-4" />
                {item.name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { companies, selectedCompany, selectCompany, isParent, canSeeAllCompanies } = useCompany();
  const { canView, isAdmin, role } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedSections, setExpandedSections] = useState(['Finance']);
  const [darkMode, setDarkMode] = useState(false);

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const toggleSection = (name) => {
    setExpandedSections((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCompanySelect = (companyId) => {
    selectCompany(companyId);
    setCompanyDropdownOpen(false);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Close dropdowns when clicking outside
  const closeDropdowns = () => {
    setOpenDropdown(null);
    setCompanyDropdownOpen(false);
  };

  // Get parent and subsidiaries
  const parentCompany = companies.find(c => c.type === 'parent');
  const subsidiaries = companies.filter(c => c.type === 'subsidiary');

  return (
    <div className="min-h-screen bg-background" onClick={closeDropdowns}>
      {/* Top Navigation Bar */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border z-50">
        <div className="flex items-center h-full px-4 gap-2 sm:gap-4">
          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 hover:bg-muted rounded-lg"
            onClick={(e) => { e.stopPropagation(); setSidebarOpen(!sidebarOpen); }}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <div className="grid grid-cols-2 gap-0.5">
              <div className="w-3 h-3 bg-[#0066B3] rounded-sm"></div>
              <div className="w-3 h-3 bg-[#00A651] rounded-sm"></div>
              <div className="w-3 h-3 bg-[#006837] rounded-sm"></div>
              <div className="w-3 h-3 bg-[#009B77] rounded-sm"></div>
            </div>
            <span className="font-semibold text-sm hidden md:inline">DEA Group, Inc.</span>
          </Link>

          {/* Company Selector (Desktop) - Only for CEO/Admin */}
          {canSeeAllCompanies && (
          <div className="relative hidden sm:block" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-md hover:bg-muted transition-colors text-sm"
              data-testid="company-selector"
            >
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="max-w-[140px] truncate font-medium">
                {selectedCompany?.name || 'Select Company'}
              </span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>

            {companyDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-lg py-2 animate-fade-in z-50">
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Parent Company
                </div>
                {parentCompany && (
                  <button
                    onClick={() => handleCompanySelect(parentCompany.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors ${
                      selectedCompany?.id === parentCompany.id ? 'bg-muted' : ''
                    }`}
                    data-testid={`company-option-${parentCompany.code}`}
                  >
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{parentCompany.name}</span>
                  </button>
                )}
                {subsidiaries.length > 0 && (
                  <>
                    <div className="my-2 border-t border-border" />
                    <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Subsidiaries
                    </div>
                    {subsidiaries.map((company) => {
                      const Icon = subsidiaryIcons[company.code] || Building2;
                      return (
                        <button
                          key={company.id}
                          onClick={() => handleCompanySelect(company.id)}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors ${
                            selectedCompany?.id === company.id ? 'bg-muted' : ''
                          }`}
                          data-testid={`company-option-${company.code}`}
                        >
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{company.name}</span>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
          )}

          {/* Show company name for non-admin users */}
          {!canSeeAllCompanies && selectedCompany && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-border rounded-md bg-muted/50 text-sm">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="max-w-[140px] truncate font-medium">
                {selectedCompany?.name}
              </span>
            </div>
          )}

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {/* Teller-only navigation */}
            {role === 'teller' ? (
              <>
                <Link
                  to="/dfs/teller-dashboard"
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    location.pathname === '/dfs/teller-dashboard' || location.pathname === '/'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid="nav-link-teller-dashboard"
                >
                  <Monitor className="h-4 w-4" />
                  My Dashboard
                </Link>
              </>
            ) : (
              <>
                {/* Full navigation for other roles */}
                {canView('dashboard') && (
                  <Link
                    to="/dashboard"
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === '/dashboard' || location.pathname === '/'
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid="nav-link-dashboard"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                )}

                {canView('finance') && (
                  <NavDropdown
                    trigger="Finance"
                    icon={Receipt}
                    items={financeNavItems}
                    isOpen={openDropdown === 'finance'}
                    onToggle={() => toggleDropdown('finance')}
                    location={location}
                  />
                )}

                {canView('hr') && (
                  <NavDropdown
                    trigger="HR"
                    icon={Users}
                    items={hrNavItems}
                    isOpen={openDropdown === 'hr'}
                    onToggle={() => toggleDropdown('hr')}
                    location={location}
                  />
                )}

                {canView('dfs') && (
                  <SubsidiaryDropdown
                    isOpen={openDropdown === 'subsidiaries'}
                    onToggle={() => toggleDropdown('subsidiaries')}
                    location={location}
                  />
                )}

                {canView('reports') && (
                  <Link
                    to="/reports"
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === '/reports'
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid="nav-link-reports"
                  >
                    <BarChart3 className="h-4 w-4" />
                    Reports
                  </Link>
                )}

                {isParent && isAdmin && (
                  <Link
                    to="/consolidation"
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname.startsWith('/consolidation')
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid="nav-link-consolidation"
                  >
                    <PieChart className="h-4 w-4" />
                    Consolidation
                  </Link>
                )}

                {canView('settings') && (
                  <Link
                    to="/settings"
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname.startsWith('/settings')
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid="nav-link-settings"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </Link>
                )}
              </>
            )}
          </nav>

          <div className="flex-1" />

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <button
              onClick={(e) => { e.stopPropagation(); toggleDarkMode(); }}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              data-testid="theme-toggle"
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {/* User Menu */}
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{role}</p>
              </div>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                title="Logout"
                data-testid="logout-button"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="fixed top-0 left-0 bottom-0 w-72 bg-card border-r border-border z-50 lg:hidden animate-fade-in">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="grid grid-cols-2 gap-0.5">
                  <div className="w-2.5 h-2.5 bg-[#0066B3] rounded-sm"></div>
                  <div className="w-2.5 h-2.5 bg-[#00A651] rounded-sm"></div>
                  <div className="w-2.5 h-2.5 bg-[#006837] rounded-sm"></div>
                  <div className="w-2.5 h-2.5 bg-[#009B77] rounded-sm"></div>
                </div>
                <span className="font-semibold text-sm">DEA Group</span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="p-4 space-y-6 overflow-y-auto h-[calc(100vh-4rem)]">
              {/* Main */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Main</p>
                <Link
                  to="/dashboard"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/dashboard' || location.pathname === '/'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <LayoutDashboard className="h-5 w-5" />
                  Dashboard
                </Link>
                <Link
                  to="/reports"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/reports'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <BarChart3 className="h-5 w-5" />
                  Reports
                </Link>
              </div>

              {/* Core Modules */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Core Modules</p>
                <SidebarSection
                  title="Finance"
                  icon={Receipt}
                  items={financeNavItems}
                  isExpanded={expandedSections.includes('Finance')}
                  onToggle={() => toggleSection('Finance')}
                  location={location}
                />
                <SidebarSection
                  title="Human Resources"
                  icon={Users}
                  items={hrNavItems}
                  isExpanded={expandedSections.includes('HR')}
                  onToggle={() => toggleSection('HR')}
                  location={location}
                />
              </div>

              {/* Subsidiaries */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Subsidiaries</p>
                {subsidiaryNavGroups.map((group) => (
                  <SidebarSection
                    key={group.name}
                    title={group.name}
                    icon={group.icon}
                    items={group.items}
                    isExpanded={expandedSections.includes(group.name)}
                    onToggle={() => toggleSection(group.name)}
                    location={location}
                    colorClass={group.color}
                  />
                ))}
              </div>

              {/* Consolidation (for parent company) */}
              {isParent && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Consolidation</p>
                  <Link
                    to="/consolidation"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname.startsWith('/consolidation')
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <PieChart className="h-5 w-5" />
                    Group Dashboard
                  </Link>
                </div>
              )}

              {/* Settings */}
              <div className="space-y-1">
                <Link
                  to="/settings"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/settings'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Settings className="h-5 w-5" />
                  Settings
                </Link>
              </div>
            </nav>
          </aside>
        </>
      )}

      {/* Main Content */}
      <main className="pt-14 min-h-screen">
        <div className="p-6">{children}</div>
      </main>
    </div>
  );
}
